import { type FC, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useContentsQuery } from "@/partner/entities/contents";
import { ContentTypeType } from "@/types/graphql/partner.generated";
import { useDebounce } from "@/shared/hooks";

interface LinkedContentOption {
  id: string;
  title: string;
}

interface LinkedContentFieldProps {
  value: string | null;
  /** Название уже выбранного контента (из linkedContent сохранённой метки). */
  valueTitle?: string | null;
  onChange: (contentId: string | null) => void;
  label?: string;
}

// Аналог референсного ContentField (Autocomplete по видео правообладателя):
// поиск контента типа VIDEO по названию для карточки в аннотации.
export const LinkedContentField: FC<LinkedContentFieldProps> = ({
  value,
  valueTitle,
  onChange,
  label,
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState("");
  const search = useDebounce(inputValue, 300);

  const { data, loading } = useContentsQuery({
    variables: {
      page: 0,
      rowsPerPage: 20,
      filter: { search: search || undefined, type: ContentTypeType.VideoType },
    },
  });

  const options: LinkedContentOption[] = (data?.contents.nodes ?? []).map(
    (node) => ({ id: String(node.id), title: node.title })
  );

  // Выбранное значение может отсутствовать в текущей странице поиска —
  // подмешиваем его, чтобы Autocomplete показывал название, а не пустоту.
  const selected: LinkedContentOption | null = value
    ? (options.find((option) => option.id === value) ?? {
        id: value,
        title: valueTitle ?? value,
      })
    : null;
  const allOptions =
    selected && !options.some((option) => option.id === selected.id)
      ? [selected, ...options]
      : options;

  return (
    <Autocomplete
      value={selected}
      onChange={(_event, newValue) => onChange(newValue?.id ?? null)}
      inputValue={inputValue}
      onInputChange={(_event, newInputValue) => setInputValue(newInputValue)}
      options={allOptions}
      getOptionLabel={(option) => option.title}
      isOptionEqualToValue={(option, val) => option.id === val.id}
      loading={loading}
      fullWidth
      renderInput={(params) => (
        <TextField
          {...params}
          label={label ?? t("videos.timecodes.linkedContent")}
          variant="outlined"
        />
      )}
    />
  );
};
